import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useProblems } from '../hooks/useProblems';
import './POTDList.css';

/* ── skeleton card ── */
function Skeleton() {
  return (
    <div className="pl__card pl__card--skel">
      <div className="pl__card-top">
        <div style={{ display:'flex', gap:8 }}>
          <div className="skel" style={{ width:36, height:16, borderRadius:6 }} />
          <div className="skel" style={{ width:54, height:16, borderRadius:20 }} />
        </div>
        <div className="skel" style={{ width:52, height:13 }} />
      </div>
      <div className="skel" style={{ height:20, marginBottom:8 }} />
      <div className="skel" style={{ height:13, width:'88%', marginBottom:5 }} />
      <div className="skel" style={{ height:13, width:'66%', marginBottom:16 }} />
      <div style={{ display:'flex', gap:6 }}>
        <div className="skel" style={{ width:60, height:20, borderRadius:20 }} />
        <div className="skel" style={{ width:72, height:20, borderRadius:20 }} />
      </div>
    </div>
  );
}

const DIFFS   = ['All', 'Easy', 'Medium', 'Hard'];
const SORTS = [
  { key: 'date-desc',  label: 'Newest first'  },
  { key: 'date-asc',   label: 'Oldest first'  },
  { key: 'diff-easy',  label: 'Easy → Hard'   },
  { key: 'diff-hard',  label: 'Hard → Easy'   },
];

function diffOrder(d = '') {
  return d.toLowerCase() === 'easy' ? 0 : d.toLowerCase() === 'medium' ? 1 : 2;
}

/* ── Pattern keyword map: HomePage key → substrings that match p.pattern ──
 * Each entry is an array so multiple keywords can match one group.
 * Verified against actual DB pattern strings.
 */
const PATTERN_KW_MAP = {
  'Greedy':              ['greedy','bubble-up'],
  'Dynamic Programming': ['dp','3-d dp'],
  'Binary Search':       ['binary search'],
  'Math':                ['math','parity'],
  'Bit Manipulation':    ['xor','bit','mask'],
  'Tree / DFS':          ['dfs','tree','path accum'],
  'Sliding Window':      ['sliding window'],
  'Backtracking':        ['backtrack'],
  'Custom Sort':         ['sort','comparator'],
  'Simulation':          ['simulation','carry'],
  'Diagonal':            ['diagonal','cantor'],
  'Graph / Kruskal':     ['kruskal','graph','mst','spanning'],
};

function patternMatches(problemPattern, groupKey) {
  const kws = PATTERN_KW_MAP[groupKey] || [groupKey.toLowerCase()];
  const pat = (problemPattern || '').toLowerCase();
  return kws.some(kw => pat.includes(kw));
}

export default function POTDList() {
  const { problems, loading } = useProblems();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search,         setSearch]         = useState('');
  const [diffFilter,     setDiffFilter]      = useState('All');
  const [activePattern,  setActivePattern]   = useState(null); /* pattern group key or null */
  const [sort,           setSort]            = useState('date-desc');

  /* On mount: read ?pattern= param set by HomePage card click */
  useEffect(() => {
    const p = searchParams.get('pattern');
    if (p) {
      setActivePattern(p);
      setSearchParams({}, { replace: true });
    }
  }, []);

  /* Build pattern groups from real problems — real-time from DB */
  const patternGroups = useMemo(() => {
    if (!problems.length) return [];
    const seen = new Set();
    const groups = [];
    Object.keys(PATTERN_KW_MAP).forEach(groupKey => {
      const count = problems.filter(p => patternMatches(p.pattern, groupKey)).length;
      if (count > 0 && !seen.has(groupKey)) {
        seen.add(groupKey);
        groups.push({ key: groupKey, count });
      }
    });
    return groups.sort((a, b) => b.count - a.count);
  }, [problems]);

  // Replace the current filtered useMemo and counts useMemo with these:

const filtered = useMemo(() => {
  let list = [...problems];

  /* 1. Filter: Pattern */
  if (activePattern) {
    list = list.filter(p => patternMatches(p.pattern, activePattern));
  }

  /* 2. Text Search: Handle negative IDs */
  const q = search.trim().toLowerCase();
  if (q) list = list.filter(p =>
    p.title?.toLowerCase().includes(q) ||
    p.tags?.some(t => t.toLowerCase().includes(q)) ||
    p.pattern?.toLowerCase().includes(q) ||
    String(p.id).includes(q)
  );

  /* 3. Difficulty */
  if (diffFilter !== 'All') list = list.filter(p => p.difficulty === diffFilter);

  /* 4. Sorting: Ensure Striver IDs (-1, -2) don't break sorting */
  if (sort === 'date-desc') list.sort((a, b) => new Date(b.date) - new Date(a.date));
  if (sort === 'date-asc')  list.sort((a, b) => new Date(a.date) - new Date(b.date));
  if (sort === 'diff-easy') list.sort((a, b) => diffOrder(a.difficulty) - diffOrder(b.difficulty));
  if (sort === 'diff-hard') list.sort((a, b) => diffOrder(b.difficulty) - diffOrder(a.difficulty));
  
  // Use Math.abs for ID sorting so # -1 is treated as a valid index rather than a "small" number
  if (sort === 'id-asc')    list.sort((a, b) => Math.abs(a.id) - Math.abs(b.id));
  if (sort === 'id-desc')   list.sort((a, b) => Math.abs(b.id) - Math.abs(a.id));

  return list;
}, [problems, activePattern, search, diffFilter, sort]);

const counts = useMemo(() => {
  const base = activePattern
    ? problems.filter(p => patternMatches(p.pattern, activePattern))
    : problems;
  return {
    All:    base.length,
    Easy:   base.filter(p => p.difficulty === 'Easy').length,
    Medium: base.filter(p => p.difficulty === 'Medium').length,
    Hard:   base.filter(p => p.difficulty === 'Hard').length,
  };
}, [problems, activePattern]);


  return (
    <main className="pl">
      <div className="container">

        {/* ── page header ── */}
        <div className="pl__hd">
          <div className="pl__hd-left">
            <div className="pl__eyebrow">// problems</div>
            <h1 className="pl__title">Problem of the Day</h1>
            <p className="pl__desc">
              Daily LeetCode problems with full solutions, visual walkthroughs,
              and clean code in Python, C++ &amp; Java.
            </p>
          </div>
          <div className="pl__hd-stats">
            <div className="pl__hd-stat">
              <span className="pl__hd-stat-n">{loading ? '—' : problems.length}</span>
              <span className="pl__hd-stat-l">Problems</span>
            </div>
            <div className="pl__hd-stat-sep" />
            <div className="pl__hd-stat">
              <span className="pl__hd-stat-n pl__hd-stat-n--easy">{loading ? '—' : counts.Easy}</span>
              <span className="pl__hd-stat-l">Easy</span>
            </div>
            <div className="pl__hd-stat-sep" />
            <div className="pl__hd-stat">
              <span className="pl__hd-stat-n pl__hd-stat-n--med">{loading ? '—' : counts.Medium}</span>
              <span className="pl__hd-stat-l">Medium</span>
            </div>
            <div className="pl__hd-stat-sep" />
            <div className="pl__hd-stat">
              <span className="pl__hd-stat-n pl__hd-stat-n--hard">{loading ? '—' : counts.Hard}</span>
              <span className="pl__hd-stat-l">Hard</span>
            </div>
          </div>
        </div>

        {/* ── pattern strip — real-time from DB ── */}
        {!loading && patternGroups.length > 0 && (
          <div className="pl__pat-strip">
            <button
              className={`pl__pat-chip ${!activePattern ? 'pl__pat-chip--on' : ''}`}
              onClick={() => setActivePattern(null)}
            >
              All patterns
            </button>
            {patternGroups.map(g => (
              <button
                key={g.key}
                className={`pl__pat-chip ${activePattern === g.key ? 'pl__pat-chip--on' : ''}`}
                onClick={() => setActivePattern(activePattern === g.key ? null : g.key)}
              >
                {g.key}
                <span className="pl__pat-cnt">{g.count}</span>
              </button>
            ))}
          </div>
        )}

        {/* ── controls bar ── */}
        <div className="pl__controls">
          {/* row 1: search + difficulty */}
          <div className="pl__controls-row">
            <div className="pl__search-wrap">
              <svg className="pl__search-icon" width="15" height="15" viewBox="0 0 15 15" fill="none">
                <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5"/>
                <line x1="10.5" y1="10.5" x2="14" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <input
                className="pl__search"
                type="text"
                placeholder="Search title, tag, pattern, number…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button className="pl__search-clear" onClick={() => setSearch('')} aria-label="Clear">✕</button>
              )}
            </div>
            <div className="pl__filters">
              {DIFFS.map(d => (
                <button
                  key={d}
                  className={`pl__filter ${diffFilter === d ? 'pl__filter--on' : ''} ${d !== 'All' ? `pl__filter--${d.toLowerCase()}` : ''}`}
                  onClick={() => setDiffFilter(d)}
                >
                  {d}
                  {!loading && (
                    <span className="pl__filter-cnt">{counts[d]}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* row 2: sort pills */}
          <div className="pl__sort-row">
            <span className="pl__sort-label">Sort:</span>
            {SORTS.map(s => (
              <button
                key={s.key}
                className={`pl__sort-pill ${sort === s.key ? 'pl__sort-pill--on' : ''}`}
                onClick={() => setSort(s.key)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── results count ── */}
        <div className="pl__meta">
          {loading ? (
            <span className="pl__meta-txt">Loading problems…</span>
          ) : (
            <span className="pl__meta-txt">
              Showing <strong>{filtered.length}</strong>
              {activePattern
                ? <> <strong>{activePattern}</strong> problems</>
                : <> of <strong>{problems.length}</strong> problems</>
              }
              {search && <> matching <em>"{search}"</em></>}
              {activePattern && (
                <button
                  className="pl__meta-clear"
                  onClick={() => setActivePattern(null)}
                >
                  ✕ clear pattern
                </button>
              )}
            </span>
          )}
        </div>

        {/* ── grid ── */}
        <div className="pl__grid">
          {loading
            ? Array(9).fill(0).map((_, i) => <Skeleton key={i} />)
            : filtered.length === 0
              ? (
                <div className="pl__empty">
                  <div className="pl__empty-icon">🔍</div>
                  <div className="pl__empty-title">No problems found</div>
                  <div className="pl__empty-sub">
                    Try a different search term or clear the filters.
                  </div>
                  <button className="pl__empty-btn" onClick={() => { setSearch(''); setDiffFilter('All'); setActivePattern(null); }}>
                    Clear filters
                  </button>
                </div>
              )
              : filtered.map((p, i) => (
                <Link
                  key={p.id}
                  to={`/potd/${p.slug}`}
                  className="pl__card"
                  style={{ animationDelay: `${Math.min(i, 8) * 0.04}s` }}
                >
                  <div className="pl__card-top">
                    <div className="pl__card-top-left">
                      <span className="pl__card-id">#{p.id}</span>
                      <span className={`pl__badge pl__badge--${p.difficulty?.toLowerCase()}`}>
                        {p.difficulty}
                      </span>
                    </div>
                    <span className="pl__card-date">{p.date}</span>
                  </div>

                  <h2 className="pl__card-title">{p.title}</h2>
                  <p className="pl__card-summary">{p.summary}</p>

                  <div className="pl__card-footer">
                    <div className="pl__tags">
                      {p.tags?.slice(0, 3).map(t => (
                        <span key={t} className="pl__tag">{t}</span>
                      ))}
                    </div>
                    <span className="pl__read-btn">Read →</span>
                  </div>
                </Link>
              ))
          }
        </div>

      </div>
    </main>
  );
}
